import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Table, {
  createColumns,
  GROUPING_RANGE_DATES,
  type ColumnFilterOption,
  type GroupingRange,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import Badge from "@/v2-backoffice-client/components/badge"
import { DotText } from "@/v2-backoffice-client/components/dot"
import LicensePlanStatusBadge from "@/v2-backoffice-client/license/plan/LicensePlanStatusBadge"
import LicenseProductBadge from "@/v2-backoffice-client/license/product/LicenseProductBadge"
import LicensePlanTableRowToolbar from "@/v2-backoffice-client/license/plan/LicensePlanTableRowToolbar"
import { FENERUM_PLAN_BASE_URL } from "@/v2-backoffice-client/constants"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import {
  LICENSE_PLAN_NO_PRODUCT,
  LICENSE_PLAN_STATUS,
  type LicensePlanStatus,
} from "@/v2-backoffice-client/license/plan/license.plan.constants"
import { useLicensePlansQuery } from "@/v2-backoffice-client/license/plan/license.plan.query"
import { getLicensePlanStatus } from "@/v2-backoffice-client/license/plan/license.plan.utils"
import {
  deriveSeatModel,
  describeSeatModel,
  formatListPrice,
  SEAT_MODEL_KIND,
  SEAT_MODEL_LABEL,
  type SeatModelText,
} from "@/v2-backoffice-client/license/plan/license.plan.seatModel.utils"
import type { LicensePlanFragment } from "@/v2-backoffice-client/license/plan/__types__/license.plan.fragment"

const BILLING_STATUS = "Billing"
const NOT_BILLING_STATUS = "Not billing"

// Monthly run-rate bands (NOK in practice) — grouping by them splits the
// catalog into the licenses that carry the book and the long tail.
const GROUPING_RANGE_MRR: GroupingRange[] = [
  { label: "Not billing", test: (v) => v == null || Number(v) <= 0 },
  { label: "< 5k", test: (v) => Number(v) < 5_000 },
  { label: "5k – 25k", test: (v) => Number(v) < 25_000 },
  { label: "25k – 100k", test: (v) => Number(v) < 100_000 },
  { label: "100k +", test: () => true },
]

// Adoption bands — grouping by them splits the catalog into dead licenses,
// the long tail and the workhorses.
const GROUPING_RANGE_CUSTOMERS: GroupingRange[] = [
  { label: "No customers", test: (v) => !v || Number(v) <= 0 },
  { label: "1 – 5", test: (v) => Number(v) <= 5 },
  { label: "6 – 20", test: (v) => Number(v) <= 20 },
  { label: "20 +", test: () => true },
]

const GROUPING_RANGE_SEATS: GroupingRange[] = [
  { label: "No seats", test: (v) => !v || Number(v) <= 0 },
  { label: "1 – 25", test: (v) => Number(v) <= 25 },
  { label: "26 – 100", test: (v) => Number(v) <= 100 },
  { label: "100 +", test: () => true },
]

// Status is derived per row (the one rule lives in license.plan.utils, shared
// with the license panel) so the pills and the column filter on it like any
// real field; the seat model likewise (license.plan.seatModel.utils, read
// off the whole catalog: a base license's seat line is another row), and
// AvgMrrPerCustomer and BillingStatus, so the value columns sort/filter/group.
type LicensePlanRow = LicensePlanFragment & {
  Status: LicensePlanStatus
  SeatModel: SeatModelText
  AvgMrrPerCustomer: number | null
  BillingStatus: typeof BILLING_STATUS | typeof NOT_BILLING_STATUS
}

const SEAT_MODEL_FILTER_OPTIONS: ColumnFilterOption[] = [
  {
    label: SEAT_MODEL_LABEL[SEAT_MODEL_KIND.Companion],
    value: SEAT_MODEL_LABEL[SEAT_MODEL_KIND.Companion],
  },
  {
    label: SEAT_MODEL_LABEL[SEAT_MODEL_KIND.Bundle],
    value: SEAT_MODEL_LABEL[SEAT_MODEL_KIND.Bundle],
  },
  {
    label: SEAT_MODEL_LABEL[SEAT_MODEL_KIND.SeatLine],
    value: SEAT_MODEL_LABEL[SEAT_MODEL_KIND.SeatLine],
  },
  {
    label: SEAT_MODEL_LABEL[SEAT_MODEL_KIND.Quantity],
    value: SEAT_MODEL_LABEL[SEAT_MODEL_KIND.Quantity],
  },
  {
    label: SEAT_MODEL_LABEL[SEAT_MODEL_KIND.Flat],
    value: SEAT_MODEL_LABEL[SEAT_MODEL_KIND.Flat],
  },
  {
    label: SEAT_MODEL_LABEL[SEAT_MODEL_KIND.Undeclared],
    value: SEAT_MODEL_LABEL[SEAT_MODEL_KIND.Undeclared],
    dotVariant: "destructive",
  },
]

function createLicensePlanColumns(input: {
  productOptions: ColumnFilterOption[]
  vatTypeOptions: ColumnFilterOption[]
}) {
  const { productOptions, vatTypeOptions } = input

  return createColumns<LicensePlanRow>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      Name: {
        id: "Name",
        label: "License",
        defaultSize: 220,
        to: ({ row }) => `?v=plan:${row.original.Id}`,
      },
      Code: {
        id: "Code",
        label: "Code",
        defaultSize: 70,
        filter: { type: "text", placeholder: "Code..." },
        getValue: (row) => row.Code ?? "",
        renderCell({ row }) {
          return row.original.Code ?? null
        },
      },
      ProductName: {
        id: "ProductName",
        label: "Product",
        defaultSize: 120,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any product",
          toolbar: true,
          options: productOptions,
        },
        getValue: (row) => row.ProductName ?? LICENSE_PLAN_NO_PRODUCT,
        renderCell({ row }) {
          const value = row.original.ProductName
          if (!value) {
            return (
              <DotText variant="destructive">{LICENSE_PLAN_NO_PRODUCT}</DotText>
            )
          }
          return <LicenseProductBadge productName={value} />
        },
        exportValue({ row }) {
          return row.original.ProductName ?? LICENSE_PLAN_NO_PRODUCT
        },
        summaryValue({ row }) {
          return row.original.ProductName ?? LICENSE_PLAN_NO_PRODUCT
        },
      },
      // Fenerum's price, read-only: the monthly term when the license has
      // one, else the shortest interval still sold. The panel's Revenue tab
      // lists every term.
      ListPrice: {
        id: "ListPrice",
        label: "List Price",
        description:
          "Fenerum's list price for one unit of this license — the monthly term when there is one, else the shortest interval still sold. Change it in Fenerum; the mirror picks it up within the hour. A per-seat line's price is per seat.",
        defaultSize: 130,
        filter: { type: "number-range" },
        renderCell({ row }) {
          const text = formatListPrice(row.original)
          if (!text) return null
          return <span className="tabular-nums">{text}</span>
        },
        exportValue({ row }) {
          return formatListPrice(row.original) ?? ""
        },
      },
      // How a customer's seat count comes about under this license: the
      // kind as a pill, the detail (included seats, the seat line and its
      // price, the bundle size) on hover and spelled out on the panel. The seat
      // line is another row of this board, so the rule reads the whole
      // catalog (license.plan.seatModel.utils).
      SeatModel: {
        id: "SeatModel",
        label: "Seat Model",
        description:
          "How seats are counted on this license. Base: a fixed price that includes some seats, the rest sold on the seat line it names. Seat bundle: a fixed price for a set number of seats, a bigger team moves to the next size. Seat line: the per-seat license itself, its quantity is the extra seats of the bases naming it. Per seat: sold per seat on its own. Company-wide: one price, no seat count. Not set: the license works, customers just get no seat cap. Hover a pill for the detail; the license panel spells it out.",
        defaultSize: 110,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any seat model",
          options: SEAT_MODEL_FILTER_OPTIONS,
        },
        getValue: (row) => row.SeatModel.label,
        renderCell({ row }) {
          const { label, detail } = row.original.SeatModel
          if (label === SEAT_MODEL_LABEL[SEAT_MODEL_KIND.Undeclared]) {
            return <DotText variant="destructive">{label}</DotText>
          }
          return (
            <Badge size="sm" tooltip={detail}>
              {label}
            </Badge>
          )
        },
        exportValue({ row }) {
          const { label, detail } = row.original.SeatModel
          return detail ? `${label} · ${detail}` : label
        },
        summaryValue({ row }) {
          return row.original.SeatModel.label
        },
      },
      // Visible by default, next to the other catalog-state columns: whether
      // a license is sold, still to be set up, or being retired is a fact an
      // operator reads off the board, not one to dig out of the columns
      // panel. The pills filter on it; the cell's hover panel carries the
      // story (what is missing, or when, who, successor, note and the
      // remaining worklist).
      Status: {
        id: "Status",
        label: "Status",
        description:
          "Live: sold and set up. Needs setup: no product yet, so the import skips its subscriptions and the row shows no customers or MRR. Deprecated: being retired; hover for when, who and the successor. Group on it to split the catalog.",
        defaultSize: 110,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any status",
          options: [
            {
              label: LICENSE_PLAN_STATUS.Live,
              value: LICENSE_PLAN_STATUS.Live,
              dotVariant: "success-1",
            },
            {
              label: LICENSE_PLAN_STATUS.NeedsSetup,
              value: LICENSE_PLAN_STATUS.NeedsSetup,
              dotVariant: "destructive",
            },
            {
              label: LICENSE_PLAN_STATUS.Deprecated,
              value: LICENSE_PLAN_STATUS.Deprecated,
              dotVariant: "warning",
            },
          ],
        },
        renderCell({ row }) {
          return <LicensePlanStatusBadge plan={row.original} />
        },
      },
      CustomerCount: {
        id: "CustomerCount",
        label: "Customers",
        defaultSize: 80,
        filter: { type: "number-range" },
        groupingRanges: GROUPING_RANGE_CUSTOMERS,
      },
      LicenseCount: {
        id: "LicenseCount",
        label: "Ever on it",
        description:
          "How many customer licenses have ever been on this license, live or ended; Customers counts the distinct customers among them.",
        defaultSize: 90,
        filter: { type: "number-range" },
      },
      LiveSeats: {
        id: "LiveSeats",
        label: "Live Seats",
        defaultSize: 80,
        filter: { type: "number-range" },
        groupingRanges: GROUPING_RANGE_SEATS,
      },
      NamedSeats: {
        id: "NamedSeats",
        label: "Named Seats",
        description:
          "People named on this license across all customers, on live licenses only (the MCP products are priced per named person). The Named Seats tab lists them; 0 when the product covers everyone.",
        defaultSize: 90,
        filter: { type: "number-range" },
        groupingRanges: GROUPING_RANGE_SEATS,
        renderCell({ row }) {
          return row.original.NamedSeats || null
        },
      },
      Mrr: {
        id: "Mrr",
        label: "MRR",
        description:
          "Live monthly run-rate: Fenerum subscriptions riding this license's terms. The license panel breaks it down.",
        defaultSize: 100,
        groupingRanges: GROUPING_RANGE_MRR,
        filter: { type: "number-range" },
        renderCell({ row }) {
          const { Mrr, MrrCurrency } = row.original
          if (Mrr == null) return null
          return (
            <span className="tabular-nums">
              {formatCompactAmount(Mrr, MrrCurrency ?? undefined)}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.Mrr ?? ""
        },
      },
      PayingCustomerCount: {
        id: "PayingCustomerCount",
        label: "Paying",
        description:
          "Customers with a live Fenerum subscription actually billing on this license's terms — the Customers column also counts trials and operator grants.",
        defaultSize: 70,
        filter: { type: "number-range" },
        renderCell({ row }) {
          return row.original.PayingCustomerCount || null
        },
        exportValue({ row }) {
          return row.original.PayingCustomerCount
        },
        // A zero the cell leaves blank is a blank, not a value to average.
        summaryValue({ row }) {
          return row.original.PayingCustomerCount || null
        },
      },
      AvgMrrPerCustomer: {
        id: "AvgMrrPerCustomer",
        label: "MRR / Customer",
        description:
          "Average monthly run-rate per paying customer — value density: a low-MRR license can still carry whales.",
        defaultSize: 110,
        defaultHidden: true,
        filter: { type: "number-range" },
        renderCell({ row }) {
          const { AvgMrrPerCustomer, MrrCurrency } = row.original
          if (AvgMrrPerCustomer == null) return null
          return (
            <span className="tabular-nums">
              {formatCompactAmount(AvgMrrPerCustomer, MrrCurrency ?? undefined)}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.AvgMrrPerCustomer ?? ""
        },
      },
      MrrAtRisk: {
        id: "MrrAtRisk",
        label: "MRR At Risk",
        description:
          "The slice of this license's MRR billed to customers currently behind on payment (overdue or in nonpayment) — sort on it to see which licenses a collections push protects.",
        defaultSize: 100,
        filter: { type: "number-range" },
        renderCell({ row }) {
          const { MrrAtRisk, MrrCurrency } = row.original
          if (!MrrAtRisk) return null
          return (
            <span className="tabular-nums text-destructive">
              {formatCompactAmount(MrrAtRisk, MrrCurrency ?? undefined)}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.MrrAtRisk ?? ""
        },
      },
      BillingStatus: {
        id: "BillingStatus",
        label: "Billing",
        description:
          "Whether any live Fenerum subscription bills on this license's terms — group on it to split the catalog into revenue-carrying and dormant licenses.",
        defaultSize: 90,
        defaultHidden: true,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any billing state",
          options: [
            {
              label: BILLING_STATUS,
              value: BILLING_STATUS,
              dotVariant: "success-1",
            },
            { label: NOT_BILLING_STATUS, value: NOT_BILLING_STATUS },
          ],
        },
        renderCell({ row }) {
          const isBilling = row.original.BillingStatus === BILLING_STATUS
          return (
            <DotText variant={isBilling ? "success-1" : "muted"}>
              {row.original.BillingStatus}
            </DotText>
          )
        },
      },
      LastAssignedAt: {
        id: "LastAssignedAt",
        label: "Last Assigned",
        description:
          "Last time ANY customer started on this license (full subscription history plus operator grants). A license nobody joined for years is a deprecation candidate.",
        defaultSize: 100,
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.LastAssignedAt
          return value ? formatDateString(value) : null
        },
        exportValue({ row }) {
          return formatDateString(row.original.LastAssignedAt)
        },
      },
      HasActiveTerms: {
        id: "HasActiveTerms",
        label: "Fenerum Sellable",
        description:
          "Whether Fenerum still has an active term to sell this license on. False = new subscriptions are impossible in Fenerum regardless of our deprecation flag.",
        defaultSize: 100,
        defaultHidden: true,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any sellability",
          options: [
            { label: "Yes", value: "Yes" },
            { label: "No", value: "No" },
          ],
        },
        getValue: (row) => (row.HasActiveTerms ? "Yes" : "No"),
        renderCell({ row }) {
          return row.original.HasActiveTerms ? "Yes" : "No"
        },
      },
      IsSelfServiceEnabled: {
        id: "IsSelfServiceEnabled",
        label: "Fenerum Self-Service",
        defaultSize: 110,
        defaultHidden: true,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any self-service",
          options: [
            { label: "Yes", value: "Yes" },
            { label: "No", value: "No" },
          ],
        },
        getValue: (row) => (row.IsSelfServiceEnabled ? "Yes" : "No"),
        renderCell({ row }) {
          return row.original.IsSelfServiceEnabled ? "Yes" : "No"
        },
      },
      VatType: {
        id: "VatType",
        label: "VAT",
        defaultSize: 80,
        defaultHidden: true,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any VAT type",
          options: vatTypeOptions,
        },
        getValue: (row) => row.VatType ?? "",
      },
      SyncedAt: {
        id: "SyncedAt",
        label: "Synced",
        defaultSize: 90,
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.SyncedAt
          return value ? formatDateString(value) : null
        },
      },
      UpdatedAt: {
        id: "UpdatedAt",
        label: "Last Edited",
        description:
          "When an operator last changed this license here (product, seat semantics or deprecation). Fenerum's own changes show under Synced.",
        defaultSize: 100,
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.UpdatedAt
          return value ? formatDateString(value) : null
        },
      },
      ExternalPlanUuid: {
        id: "ExternalPlanUuid",
        label: "Fenerum Uuid",
        defaultSize: 250,
        defaultHidden: true,
        to: ({ row }) =>
          `${FENERUM_PLAN_BASE_URL}/${row.original.ExternalPlanUuid}/`,
      },
      Id: { id: "Id", label: "Id", defaultSize: 50, defaultHidden: true },
    },
  })
}

export type LicensePlanTableProps = Omit<
  TableProps<LicensePlanRow>,
  "data" | "columns"
>

// The license catalog board: Fenerum's mirrored licenses plus what we own on
// each (product + seat semantics, deprecation) and what hangs off it. Doubles
// as the setup worklist — the proc sorts licenses without a product or seat
// semantics first, and the Needs setup pill isolates the ones without a
// product (the ones the import skips). Owns the licenses
// query (shared via the Apollo cache with the tab trigger's chip and the
// license panel).
function LicensePlanTable(props: LicensePlanTableProps) {
  const { className, ...tableProps } = props

  const { data, previousData, loading, error, refetch } = useLicensePlansQuery()
  // previousData keeps rows rendered through post-save evictions.
  const plans = (data ?? previousData)?.getLicensePlans ?? []

  const rows = useMemo<LicensePlanRow[]>(
    () =>
      plans.map((plan) => ({
        ...plan,
        Status: getLicensePlanStatus(plan),
        SeatModel: describeSeatModel(deriveSeatModel(plan, plans)),
        AvgMrrPerCustomer:
          plan.Mrr != null && plan.PayingCustomerCount > 0
            ? plan.Mrr / plan.PayingCustomerCount
            : null,
        BillingStatus:
          (plan.Mrr ?? 0) > 0 ? BILLING_STATUS : NOT_BILLING_STATUS,
      })),
    [plans],
  )

  const statusCounts = useMemo(() => {
    const counts: Record<LicensePlanStatus, number> = {
      [LICENSE_PLAN_STATUS.Live]: 0,
      [LICENSE_PLAN_STATUS.NeedsSetup]: 0,
      [LICENSE_PLAN_STATUS.Deprecated]: 0,
    }
    for (const row of rows) counts[row.Status] += 1
    return counts
  }, [rows])

  // The product/VAT filters' options come from the rows themselves — both
  // catalogs are small and this keeps the filters honest about what exists.
  const productOptions = useMemo(() => {
    const names = new Set<string>()
    for (const plan of plans) {
      if (plan.ProductName) names.add(plan.ProductName)
    }
    return [
      ...[...names].sort().map((name) => ({ label: name, value: name })),
      {
        label: LICENSE_PLAN_NO_PRODUCT,
        value: LICENSE_PLAN_NO_PRODUCT,
        dotVariant: "destructive" as const,
      },
    ]
  }, [plans])

  const vatTypeOptions = useMemo(() => {
    const types = new Set<string>()
    for (const plan of plans) {
      if (plan.VatType) types.add(plan.VatType)
    }
    return [...types].sort().map((type) => ({ label: type, value: type }))
  }, [plans])

  const columns = useMemo(
    () => createLicensePlanColumns({ productOptions, vatTypeOptions }),
    [productOptions, vatTypeOptions],
  )

  return (
    <Table
      className={cn("license-plan-table", className)}
      tableId="license-plan-table"
      data={rows}
      columns={columns}
      enableGrouping
      {...tableProps}
    >
      <Table.Toolbar>
        {/* Namespaced: three boards share the Licensing page, so the default
            "status" param would leak pill state between tabs. */}
        <Table.StatusFilter
          column="Status"
          urlParam="plans-status"
          isLoading={loading}
        >
          <Table.StatusFilter.Option value={null} count={rows.length}>
            All
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={1}
            filterValue={LICENSE_PLAN_STATUS.Live}
            variant="success"
            iconSrc="check-circle"
            count={statusCounts[LICENSE_PLAN_STATUS.Live]}
          >
            {LICENSE_PLAN_STATUS.Live}
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={0}
            filterValue={LICENSE_PLAN_STATUS.NeedsSetup}
            variant="destructive"
            iconSrc="exclamation"
            count={statusCounts[LICENSE_PLAN_STATUS.NeedsSetup]}
          >
            {LICENSE_PLAN_STATUS.NeedsSetup}
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={2}
            filterValue={LICENSE_PLAN_STATUS.Deprecated}
            variant="warning"
            iconSrc="archive"
            count={statusCounts[LICENSE_PLAN_STATUS.Deprecated]}
          >
            {LICENSE_PLAN_STATUS.Deprecated}
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />
        <Table.Search column="Name" placeholder="Search by license name..." />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu
          onRefresh={() => void refetch()}
          exportFileName="licenses"
        />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.RowToolbar<LicensePlanRow> column="Name">
        {(plan) => <LicensePlanTableRowToolbar plan={plan} />}
      </Table.RowToolbar>

      <Table.Content isLoading={loading} error={error} />
    </Table>
  )
}

export default LicensePlanTable
